import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowBack as ArrowBackIcon,
  Folder as FolderIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  BrokenImage as BrokenImageIcon,
} from '@mui/icons-material/';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';

const ROOT_PATH = '/Screenshots/';

const getBackendBase = () => {
  const h = window.location.hostname;
  if (h.includes('stage02')) return 'https://stage02-operator.vpfscandinavia.com';
  if (h === 'localhost' || h.includes('stage01'))
    return 'https://stage01-operator.vpfscandinavia.com';
  return 'https://operator.vpfscandinavia.com';
};

const parentPath = (path) => {
  const trimmed = path.replace(/\/$/, '');
  const idx = trimmed.lastIndexOf('/');
  return trimmed.slice(0, idx + 1) || ROOT_PATH;
};

// Date-2026-07-19_Time-12-36-08_GameID-03_ROP-01_User-7104_Movement-Login.jpg
const parseShotName = (name) => {
  const grab = (key) => {
    const m = name.match(new RegExp(`${key}-([^_.]+(?:-[^_.]+)*)`));
    return m ? m[1] : null;
  };
  const date = grab('Date');
  const time = grab('Time');
  return {
    date,
    time: time ? time.replace(/-/g, ':') : null,
    gameId: grab('GameID'),
    rop: grab('ROP'),
    user: grab('User'),
    movement: grab('Movement'),
  };
};

const shotCaption = (name) => {
  const p = parseShotName(name);
  const label =
    p.movement || (p.rop ? `ROP ${p.rop}` : p.gameId ? `Game ${p.gameId}` : '');
  return [label, p.time].filter(Boolean).join(' · ') || name;
};

// Dependency-free windowed grid: only the thumbnails in the viewport are mounted
const GRID_GAP = 12;
const GRID_MIN_CELL = 200;
const GRID_CELL_H = 168; // thumb (132) + caption (~36)
const GRID_ROW_H = GRID_CELL_H + GRID_GAP;
const GRID_PAD = 12;

function VirtualThumbGrid({ images, fileUrl, onOpen }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [scrollTop, setScrollTop] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () =>
      setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Reset scroll position when the folder (image set) changes.
  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [images]);

  const onScroll = () => {
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0;
      if (containerRef.current) setScrollTop(containerRef.current.scrollTop);
    });
  };
  useEffect(() => () => window.cancelAnimationFrame(rafRef.current), []);

  const avail = Math.max(0, size.width - GRID_PAD * 2);
  const cols = Math.max(1, Math.floor((avail + GRID_GAP) / (GRID_MIN_CELL + GRID_GAP)));
  const cellW = (avail - GRID_GAP * (cols - 1)) / cols;
  const rows = Math.ceil(images.length / cols);
  const totalHeight = GRID_PAD * 2 + rows * GRID_ROW_H;

  const overscan = 2;
  const startRow = Math.max(0, Math.floor(scrollTop / GRID_ROW_H) - overscan);
  const endRow = Math.min(
    rows - 1,
    Math.ceil((scrollTop + size.height) / GRID_ROW_H) + overscan
  );

  const cells = [];
  if (size.width > 0 && images.length > 0) {
    for (let r = startRow; r <= endRow; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const idx = r * cols + c;
        if (idx >= images.length) break;
        const img = images[idx];
        cells.push(
          <Box
            key={img.href}
            onClick={() => onOpen(idx)}
            sx={{
              position: 'absolute',
              top: GRID_PAD + r * GRID_ROW_H,
              left: GRID_PAD + c * (cellW + GRID_GAP),
              width: cellW,
              height: GRID_CELL_H,
              cursor: 'pointer',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #2a3a6e',
              backgroundColor: '#232a45',
              transition: 'box-shadow .15s, transform .15s',
              '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
            }}
          >
            <Box
              sx={{
                height: 132,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                backgroundColor: '#222',
              }}
            >
              <img
                src={fileUrl(img.href)}
                alt={img.name}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            <Typography
              noWrap
              sx={{ fontSize: 13, fontWeight: 600, color: '#e6eaf2', px: 1, py: 0.75 }}
            >
              {shotCaption(img.name)}
            </Typography>
          </Box>
        );
      }
    }
  }

  return (
    <Box
      ref={containerRef}
      className="sp-scroll"
      onScroll={onScroll}
      sx={{ flex: 1, overflow: 'auto', position: 'relative' }}
    >
      <Box sx={{ position: 'relative', height: totalHeight }}>{cells}</Box>
    </Box>
  );
}

const shotTitle = (name) => {
  const p = parseShotName(name);
  const parts = [];
  if (p.gameId) parts.push(`Game ${p.gameId}`);
  if (p.rop) parts.push(`ROP ${p.rop}`);
  if (p.movement) parts.push(p.movement);
  if (p.user) parts.push(`User ${p.user}`);
  const when = [p.date, p.time].filter(Boolean).join(' ');
  if (when) parts.push(when);
  return parts.join(' · ') || name;
};

function TerminalScreenshotsViewer({ ip, deviceType, id, onClose }) {
  const { t } = useTranslation();
  const base = getBackendBase();

  const [path, setPath] = useState(ROOT_PATH);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);

  const fullscreenRef = useRef(null);

  const fileUrl = useCallback(
    (href) =>
      `${base}/api/terminal-screenshot-file?ip=${ip}&path=${encodeURIComponent(
        path + href
      )}`,
    [base, ip, path]
  );

  const fetchList = useCallback(
    async (targetPath) => {
      setLoading(true);
      setError(false);
      setViewerIndex(null);
      try {
        const res = await fetch(
          `${base}/api/terminal-screenshots?ip=${ip}&path=${encodeURIComponent(
            targetPath
          )}`
        );
        if (!res.ok) throw new Error('bad response');
        const data = await res.json();
        setEntries(Array.isArray(data.entries) ? data.entries : []);
      } catch {
        setEntries([]);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [base, ip]
  );

  useEffect(() => {
    fetchList(path);
  }, [path, fetchList]);

  const folders = useMemo(() => entries.filter((e) => e.isDir), [entries]);
  const images = useMemo(() => {
    // Filenames lead with Date-…_Time-…, so a raw-name compare already sorts newest first
    const sortKey = (e) => {
      const p = parseShotName(e.name);
      return p.date ? `${p.date} ${p.time || ''}` : e.name;
    };
    return entries
      .filter((e) => !e.isDir)
      .sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
  }, [entries]);
  const atRoot = path === ROOT_PATH;

  const goBack = () => {
    if (viewerIndex !== null) {
      setViewerIndex(null);
    } else if (!atRoot) {
      setPath(parentPath(path));
    } else {
      onClose();
    }
  };

  const showPrev = useCallback(
    () =>
      setViewerIndex((i) =>
        i === null ? i : (i - 1 + images.length) % images.length
      ),
    [images.length]
  );
  const showNext = useCallback(
    () => setViewerIndex((i) => (i === null ? i : (i + 1) % images.length)),
    [images.length]
  );

  // Keyboard navigation while the lightbox is open.
  useEffect(() => {
    if (viewerIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
      else if (e.key === 'Escape') setViewerIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewerIndex, showPrev, showNext]);

  const enterFullscreen = () => {
    const el = fullscreenRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen();
  };

  const headerTitle = atRoot
    ? t('sales.terminalsScreenshotsTitle', { deviceType, id })
    : decodeURIComponent(path.replace(ROOT_PATH, '').replace(/\/$/, ''));

  const renderBody = () => {
    if (loading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <BrokenImageIcon sx={{ fontSize: 64, color: '#4a5578' }} />
          <Typography sx={{ color: '#8fa3d4' }}>
            {t('sales.terminalsScreenshotsError')}
          </Typography>
          <IconButton color="primary" onClick={() => fetchList(path)}>
            <RefreshIcon />
          </IconButton>
        </Box>
      );
    }
    if (atRoot) {
      if (folders.length === 0) {
        return (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#8fa3d4',
            }}
          >
            {t('sales.noData')}
          </Box>
        );
      }
      return (
        <Box
          className="sp-scroll"
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 2,
            p: 1,
            alignContent: 'start',
          }}
        >
          {folders.map((folder) => (
            <Box
              key={folder.href}
              onClick={() => setPath(path + folder.href)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 3,
                borderRadius: 2,
                cursor: 'pointer',
                border: '1px solid #2a3a6e',
                backgroundColor: '#232a45',
              }}
            >
              <FolderIcon sx={{ fontSize: 56, color: '#f4b400' }} />
              <Typography sx={{ fontWeight: 600, color: '#e6eaf2' }}>
                {folder.name}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    // Inside a game folder: thumbnail grid.
    if (images.length === 0) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8fa3d4',
          }}
        >
          {t('sales.noData')}
        </Box>
      );
    }
    return (
      <VirtualThumbGrid
        images={images}
        fileUrl={fileUrl}
        onOpen={setViewerIndex}
      />
    );
  };

  const current = viewerIndex !== null ? images[viewerIndex] : null;

  return (
    <div
      className="react-confirm-alert-body"
      style={{
        width: '90vw',
        height: '90vh',
        maxWidth: '90vw',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header with breadcrumb-aware back button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
          flexShrink: 0,
        }}
      >
        <IconButton color="primary" onClick={goBack} size="medium">
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#e6eaf2' }}>
          {headerTitle}
        </Typography>
        {!atRoot && (
          <Typography sx={{ fontSize: 15, color: '#8fa3d4', ml: 1 }}>
            {images.length ? `(${images.length})` : ''}
          </Typography>
        )}
      </Box>

      {renderBody()}

      {/* Lightbox overlay */}
      {current && (
        <Box
          ref={fullscreenRef}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.94)',
            borderRadius: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 5,
          }}
        >
          {/* Lightbox toolbar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            <Typography
              noWrap
              sx={{ flex: 1, fontSize: 16, fontWeight: 600 }}
              title={current.name}
            >
              {shotTitle(current.name)}
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#bbb', mr: 1 }}>
              {viewerIndex + 1} / {images.length}
            </Typography>
            <Tooltip title={t('sales.terminalsScreenshotsFullscreen')}>
              <IconButton sx={{ color: '#fff' }} onClick={enterFullscreen}>
                <FullscreenIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('sales.close')}>
              <IconButton sx={{ color: '#fff' }} onClick={() => setViewerIndex(null)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Image area with prev/next arrows */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              minHeight: 0,
              px: { xs: 1, sm: 8 },
              pb: 2,
            }}
          >
            {images.length > 1 && (
              <IconButton
                onClick={showPrev}
                sx={{
                  position: 'absolute',
                  left: 8,
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
                }}
                size="large"
              >
                <ChevronLeftIcon sx={{ fontSize: 40 }} />
              </IconButton>
            )}

            <img
              src={fileUrl(current.href)}
              alt={current.name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />

            {images.length > 1 && (
              <IconButton
                onClick={showNext}
                sx={{
                  position: 'absolute',
                  right: 8,
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
                }}
                size="large"
              >
                <ChevronRightIcon sx={{ fontSize: 40 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
    </div>
  );
}

export default TerminalScreenshotsViewer;
